/**
 * billingCalc.js — Shared billing math + validation for Menuron POS (DEV feature).
 *
 * Business rules (frozen):
 *   1. Subtotal      = sum(order_items.total_price)            [passed in as `subtotal`]
 *   2. DiscountAmt   = based on discountType + discountValue; clamped [0, subtotal]
 *   3. TaxableAmount = Subtotal − DiscountAmount
 *   4. GSTAmount     = TaxableAmount × appliedGstPercent / 100 (if gstEnabled)
 *   5. SCAmount      = TaxableAmount × appliedScPercent  / 100 (if scEnabled)
 *   6. TotalPayable  = TaxableAmount + GSTAmount + SCAmount
 *   All monetary values rounded to 2 decimal places.
 *
 * Override rules:
 *   - Manager may REDUCE gstPercent or set to 0; cannot increase above restaurant default.
 *   - Manager may REDUCE serviceChargePercent or set to 0; cannot increase above restaurant default.
 *   - Discount can be freely modified per order.
 *   - Once invoice is generated, values are locked on the order row.
 */

// ─── Internal ────────────────────────────────────────────────────────────────

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Core computation ─────────────────────────────────────────────────────────

/**
 * computeBill — flat signature used by billing screen.
 *
 * @param {number} subtotal               - sum of order_items (before discount)
 * @param {string} discountType           - 'NONE' | 'FIXED' | 'PERCENT'
 * @param {number} discountValue          - ₹ amount or % depending on type
 * @param {number} appliedGstPercent      - manager-applied rate (0 if disabled/removed)
 * @param {number} appliedScPercent       - manager-applied rate (0 if disabled/removed)
 * @returns {{ discountAmount, taxableAmount, gstAmount, serviceChargeAmount, totalPayable }}
 */
export function computeBill(subtotal, discountType, discountValue, appliedGstPercent, appliedScPercent) {
  const gross = round2(parseFloat(subtotal) || 0);
  const gstPct = Math.max(0, parseFloat(appliedGstPercent) || 0);
  const scPct  = Math.max(0, parseFloat(appliedScPercent)  || 0);

  let discountAmount = 0;
  if (discountType === 'FIXED') {
    discountAmount = round2(Math.max(0, Math.min(parseFloat(discountValue) || 0, gross)));
  } else if (discountType === 'PERCENT') {
    const pct = Math.max(0, Math.min(parseFloat(discountValue) || 0, 100));
    discountAmount = round2(gross * pct / 100);
  }

  const taxableAmount       = round2(gross - discountAmount);
  const gstAmount           = round2(taxableAmount * gstPct / 100);
  const serviceChargeAmount = round2(taxableAmount * scPct  / 100);
  const totalPayable        = round2(taxableAmount + gstAmount + serviceChargeAmount);

  return { discountAmount, taxableAmount, gstAmount, serviceChargeAmount, totalPayable };
}

/**
 * calculateBill — item-array variant used internally by the billing panel.
 * Delegates to computeBill after summing items.
 *
 * @param {object} params
 * @param {Array}  params.items                 [{unit_price, quantity}]
 * @param {string} params.discountType
 * @param {number} params.discountValue
 * @param {boolean} params.gstEnabled           used to zero out gstPercent when disabled
 * @param {number}  params.appliedGstPercent
 * @param {boolean} params.serviceChargeEnabled used to zero out scPercent when disabled
 * @param {number}  params.appliedScPercent
 */
export function calculateBill({
  items = [],
  discountType = 'NONE',
  discountValue = 0,
  gstEnabled = false,
  appliedGstPercent = 0,
  serviceChargeEnabled = false,
  appliedScPercent = 0,
}) {
  const gross = round2(items.reduce((s, i) => s + i.unit_price * i.quantity, 0));
  const effectiveGst = gstEnabled ? appliedGstPercent : 0;
  const effectiveSc  = serviceChargeEnabled ? appliedScPercent : 0;
  const result = computeBill(gross, discountType, discountValue, effectiveGst, effectiveSc);
  return { gross, ...result };
}

// ─── Override validation ──────────────────────────────────────────────────────

/**
 * validateOverrides — checks that applied GST / SC don't exceed restaurant defaults.
 * Returns a structured result so callers can surface per-field errors.
 *
 * @param {{ gstPercent: number, serviceChargePercent: number }} defaults
 * @param {{ appliedGstPercent: number, appliedScPercent: number }} applied
 * @returns {{ valid: boolean, errors: { gst?: string, sc?: string } }}
 */
export function validateOverrides(defaults, applied) {
  const errors = {};

  const dGst = parseFloat(defaults.gstPercent) || 0;
  const aGst = parseFloat(applied.appliedGstPercent);
  if (isNaN(aGst) || aGst < 0) {
    errors.gst = 'GST % must be a number ≥ 0.';
  } else if (aGst > dGst) {
    errors.gst = `GST % cannot exceed the restaurant default of ${dGst}%.`;
  }

  const dSc = parseFloat(defaults.serviceChargePercent) || 0;
  const aSc = parseFloat(applied.appliedScPercent);
  if (isNaN(aSc) || aSc < 0) {
    errors.sc = 'Service charge % must be a number ≥ 0.';
  } else if (aSc > dSc) {
    errors.sc = `Service charge % cannot exceed the restaurant default of ${dSc}%.`;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * isValidOverride — single-value check (used in BillingPanel input guards).
 * Returns true if applied ≤ default and applied ≥ 0.
 */
export function isValidOverride(applied, defaultVal) {
  const a = parseFloat(applied);
  const d = parseFloat(defaultVal);
  return !isNaN(a) && a >= 0 && a <= d;
}

/**
 * hasOverride — returns true when any applied value differs from the restaurant default.
 */
export function hasOverride({ appliedGstPercent, defaultGstPercent, appliedScPercent, defaultScPercent }) {
  return (
    parseFloat(appliedGstPercent) !== parseFloat(defaultGstPercent) ||
    parseFloat(appliedScPercent)  !== parseFloat(defaultScPercent)
  );
}

// ─── Settings shape ───────────────────────────────────────────────────────────

/**
 * BILLING_DEFAULTS — the zero-state returned when no settings row exists.
 * Components and callers should fall back to this.
 */
export const BILLING_DEFAULTS = {
  gstEnabled: false,
  gstPercent: 0,
  gstin: '',
  serviceChargeEnabled: false,
  serviceChargePercent: 0,
  defaultDiscountEnabled: false,
  defaultDiscountType: 'NONE',
  defaultDiscountValue: 0,
};

/**
 * normalizeBillingSettings — converts a raw DB row from restaurant_billing_settings
 * into the canonical shape used by all billing components.
 *
 * @param {object|null} row - raw Supabase row (or null)
 * @returns {typeof BILLING_DEFAULTS}
 */
export function normalizeBillingSettings(row) {
  if (!row) return { ...BILLING_DEFAULTS };
  return {
    gstEnabled:             row.gst_enabled              ?? false,
    gstPercent:             parseFloat(row.gst_percent)  || 0,
    gstin:                  row.gstin                    || '',
    serviceChargeEnabled:   row.service_charge_enabled   ?? false,
    serviceChargePercent:   parseFloat(row.service_charge_percent) || 0,
    defaultDiscountEnabled: row.discount_enabled         ?? false,
    defaultDiscountType:    row.discount_type            || 'NONE',
    defaultDiscountValue:   parseFloat(row.discount_value) || 0,
  };
}
